import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, Download, AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BudgetCategory {
  id: string;
  name: string;
  allocated: number;
  spent: number;
  remaining: number;
  status: 'on-track' | 'at-risk' | 'over-budget';
}

const mockBudgetCategories: BudgetCategory[] = [
  {
    id: "1",
    name: "Conservation Projects",
    allocated: 500000,
    spent: 350000,
    remaining: 150000,
    status: 'on-track'
  },
  {
    id: "2",
    name: "Staff Salaries",
    allocated: 300000,
    spent: 280000,
    remaining: 20000,
    status: 'at-risk'
  },
  {
    id: "3",
    name: "Facility Maintenance",
    allocated: 200000,
    spent: 210000,
    remaining: -10000,
    status: 'over-budget'
  },
  // Add more categories...
];

export function BudgetManagement() {
  const [period, setPeriod] = useState("2024");

  const getStatusColor = (status: BudgetCategory['status']) => {
    switch (status) {
      case 'on-track': return 'text-green-600';
      case 'at-risk': return 'text-yellow-600';
      case 'over-budget': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getProgressColor = (spent: number, allocated: number) => {
    const percentage = (spent / allocated) * 100;
    if (percentage > 100) return 'bg-red-500';
    if (percentage > 90) return 'bg-yellow-500';
    return 'bg-green-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Budget Planning</h2>
          <p className="text-gray-500">Manage and track your budget allocations</p>
        </div>
        <div className="flex gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2023">2023</SelectItem>
              <SelectItem value="2022">2022</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Budget
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Budget
          </Button>
        </div>
      </div>

      {/* Budget Overview */}
      <div className="grid grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-500">
              Total Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              $1,500,000
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-500">
              Total Spent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              $840,000
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-500">
              Remaining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              $660,000
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {mockBudgetCategories.map((category) => (
              <div key={category.id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{category.name}</h3>
                    <p className="text-sm text-gray-500">
                      ${category.spent.toLocaleString()} of ${category.allocated.toLocaleString()}
                    </p>
                  </div>
                  <div className={`flex items-center gap-2 ${getStatusColor(category.status)}`}>
                    {category.status === 'over-budget' && <AlertCircle className="w-4 h-4" />}
                    <span className="text-sm font-medium">
                      {category.status === 'over-budget' ? 'Over Budget' : 
                       category.status === 'at-risk' ? 'At Risk' : 'On Track'}
                    </span>
                  </div>
                </div>
                <Progress 
                  value={(category.spent / category.allocated) * 100} 
                  className={getProgressColor(category.spent, category.allocated)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}